import { fixtureElementCount } from './panelElements';
import type { RigSnapshot } from './types';

/**
 * The keep-last drag sample — the one thing this console writes straight into the database.
 *
 * A gesture used to be a stream of relay callables, one every 100 ms, each of which held a cloud
 * function for its whole life and none of which could coalesce. It is now a single slot per control that
 * the console overwrites as the finger moves and the Pi applies every value of. This module owns the
 * SHAPE of that slot and nothing else: building one, encoding what it targets, and saying when a sample
 * cannot be one. The write itself, and the decision to make it, live in `VenueProvider`.
 *
 * Shape owned by `2026-09-17-structural-sync-round.md` D-3 and enforced by `database.rules.json`.
 */

/** Every intent slot this console writes declares this schema; the database rules require it. */
export const CONTROLLER_INTENT_SCHEMA = 'controller-intent/1';

/**
 * The largest `params` document one drag sample may carry, in bytes of JSON.
 *
 * With the selection mask below, the encoded size is a function of the room's TOPOLOGY — how many
 * devices a gesture touches and how long their ids are — and never of the shape of the selection. So
 * this ceiling is bounded by the fixture catalog rather than by the pixel count, and 16 KiB is very
 * large headroom: a whole-room Alchemy drag (2,904 elements over ~30 devices) encodes to about 1.1 KB,
 * and a 4,000-element room of the same device count to about 1.4 KB — roughly 9% of the cap either way.
 *
 * Over the ceiling the samples simply do not go: the rules would refuse the write, so it is not made.
 * The release still commits the final value atomically and correctly through the command queue, and the
 * console records the skip once per gesture. There is deliberately NO fallback to the relay callable for
 * an oversized sample — that is the path this round exists to delete, and a fallback would keep it alive
 * for exactly the rooms that most need it gone.
 */
export const INTENT_MAX_PARAMS_BYTES = 16_384;

/**
 * The moment a drag sample was made — resolved by the DATABASE, never by the browser that wrote it.
 *
 * Typed as the opaque server-timestamp sentinel rather than as a number, and that is the whole point: a
 * `number` here would compile perfectly well with `Date.now()` in it, which is precisely the mistake
 * this field exists to make impossible. No client clock enters the system.
 *
 * Everything downstream that needs a time reads this one: the Pi computes the sample's expiry as
 * `at + INTENT_TTL` from its own side, and the rules' rate floor compares one server stamp against
 * another. A console on a machine whose clock is minutes out is therefore neither refused, nor
 * rate-limited out of its own drag, nor able to claim a sample is newer than it is.
 */
export type ServerStampedAt = object;

/**
 * One keep-last drag sample, exactly as it is stored at `controllerIntent/{deviceId}/{controlKey}`.
 *
 * There is no `expiresAt`. A lifetime the writer states is a lifetime the writer can lie about, and it
 * would have to be computed from the same browser clock `at` no longer trusts. The TTL is a constant
 * both the rules and the Pi already hold, so the slot carries the one fact only the writer knows — what
 * the operator did — and nothing else. A slot carrying an `expiresAt` is invalid, exactly as one
 * carrying `elements` is.
 */
export interface ControllerIntent {
  schema: typeof CONTROLLER_INTENT_SCHEMA;
  uid: string;
  command: string;
  paramsJson: string;
  at: ServerStampedAt;
}

/** Element counts by device id, the one number a selection mask has to be sized against. */
export type ElementCounts = ReadonlyMap<string, number>;

/** The element counts of every controllable fixture in a rig. */
export function rigElementCounts(rig: RigSnapshot | undefined): ElementCounts {
  const counts = new Map<string, number>();
  for (const fixture of rig?.fixtures ?? []) {
    if (fixture.isControllable === false) continue;
    counts.set(fixture.deviceId, fixtureElementCount(fixture));
  }
  return counts;
}

function base64(bytes: Uint8Array): string {
  let binary = '';
  for (const byte of bytes) binary += String.fromCharCode(byte);
  return btoa(binary);
}

/**
 * The selected elements of one device as a bitmap: bit *i* of byte *i/8*, least significant bit first,
 * over exactly `ceil(elementCount / 8)` bytes.
 *
 * **Why a bitmap and not runs or an index list.** An index list is linear in the number of selected
 * pixels, so a whole-room drag on a large room runs past any sane ceiling. Inclusive runs fix the
 * contiguous case and make the SCATTERED case worse — alternating pixels on a 4,000-element room is
 * 2,000 runs, back at the ceiling, and that is an entirely ordinary Scenes gesture. A bitmap's size is a
 * function of the room alone: every selection shape over the same fixtures costs the same, so there is
 * no selection an operator can make that is expensive.
 *
 * Bits at or beyond the device's element count are always zero, which is what makes the mask's length
 * self-describing and lets the Pi refuse one that is too long.
 */
export function elementMask(indices: readonly number[], elementCount: number): string {
  const bytes = new Uint8Array(Math.ceil(Math.max(1, elementCount) / 8));
  for (const index of indices) {
    if (!Number.isInteger(index) || index < 0 || index >= elementCount) continue;
    bytes[index >> 3] |= 1 << (index & 7);
  }
  return base64(bytes);
}

/**
 * The params of a command, with its `elements` target replaced by a per-device selection mask.
 *
 * ```
 * elementMask: { "<deviceId>": "<base64 of ceil(elementCount_d / 8) bytes>" }
 * ```
 *
 * **Only devices with at least one selected element get an entry**, which is what keeps a single-pixel
 * drag at about forty bytes instead of costing a whole room. The worst case, for anyone sizing a room:
 *
 * ```
 * bytes ≈ Σ over TOUCHED devices [ len(deviceId) + ~8 punctuation + ceil(4/3 · ceil(elements_d / 8)) ]
 * ```
 *
 * At our rig sizes the device IDS dominate, not the pixels — thirty Alchemy-length ids alone are about
 * 700 bytes — so the number to watch when a room grows is how many fixtures a gesture can touch.
 *
 * **The field is named `elementMask` and deliberately not `elements`**, so the two shapes can never be
 * mistaken for one another: an intent payload carrying `elements` at all is invalid, and a reader that
 * meets one knows it is reading something that was never encoded for it.
 *
 * **Keying by device id is a deliberate trade against a flat room-wide bitmap.** A flat mask over a
 * canonical global element ordering would be about 700 bytes rather than 1.1 KB, but it requires the
 * console and the Pi to agree on that ordering — and on every rig reload — and a disagreement there
 * moves the WRONG PIXELS, silently, while both sides believe they are correct. Device ids cost about
 * 800 bytes and cannot fail that way: an id the Pi does not know is simply refused.
 *
 * Only the intent slot speaks this encoding. The committed RELEASE goes through the command queue
 * carrying today's `elements` index lists untouched, so the iOS app, the venue-network decode and every
 * other reader of `set_fixture_params` are unaffected and no mutation-critical path changes shape.
 */
export function maskIntentParams(
  params: Record<string, unknown>,
  counts: ElementCounts,
): Record<string, unknown> | undefined {
  const elements = params.elements;
  if (!elements || typeof elements !== 'object' || Array.isArray(elements)) return params;
  const masks: Record<string, string> = {};
  for (const [deviceId, value] of Object.entries(elements as Record<string, unknown>)) {
    if (!Array.isArray(value)) return undefined;
    const indices = value.filter((index): index is number => Number.isInteger(index) && index >= 0);
    if (!indices.length) continue;
    const elementCount = counts.get(deviceId);
    // A device this console cannot size cannot be masked, and guessing the length from the largest
    // selected index would produce a mask the Pi is right to refuse. The sample is skipped; the release
    // still carries the ordinary index list, so the value lands.
    if (!elementCount) return undefined;
    masks[deviceId] = elementMask(indices, elementCount);
  }
  const masked: Record<string, unknown> = { ...params, elementMask: masks };
  delete masked.elements;
  return masked;
}

/**
 * Build the slot for one drag sample, or say that this sample cannot be one.
 *
 * `undefined` means the sample could not be encoded — its params are over
 * `INTENT_MAX_PARAMS_BYTES`, or they target a device this console cannot size. Neither is an error to
 * report to an operator: the gesture is still committed by its release. Both ARE things a maintainer
 * must be able to see, so the caller records them once per gesture.
 *
 * `at` is passed in rather than read here, so this module stays free of the database SDK and can be
 * exercised without one. The caller passes the server-timestamp sentinel; nothing may pass a clock.
 */
export function controllerIntentSlot(
  uid: string,
  command: string,
  params: Record<string, unknown>,
  counts: ElementCounts,
  at: ServerStampedAt,
): ControllerIntent | undefined {
  const masked = maskIntentParams(params, counts);
  if (!masked) return undefined;
  const paramsJson = JSON.stringify(masked);
  if (new TextEncoder().encode(paramsJson).length > INTENT_MAX_PARAMS_BYTES) return undefined;
  return { schema: CONTROLLER_INTENT_SCHEMA, uid, command, paramsJson, at };
}
